// Auto-generated lightweight class for RequirementExtractionTask
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { RequirementExtractionTaskReadAPI, RequirementExtractionTaskWriteAPI, RequirementExtractionTaskReadAPISchema, RequirementExtractionTaskWriteAPISchema } from '../types/requirementextractiontask';
import { ModelRegistry } from '../../registries/model-registry';
import { AnalysesContent, AtScope, EditsNode, ExtractsNode, HasThread, IslandHoldsPin, ReferencesNode } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { TaskHistory } from '../../llm_task/api/taskhistory';
import { ScopedNode } from '../../shared/api/scopednode';
import { IslandPin } from '../../rflp/api/islandpin';



export interface RequirementExtractionTask extends Partial<RequirementExtractionTaskReadAPI> {}
export class RequirementExtractionTask extends NeoNodeBase<RequirementExtractionTaskReadAPI, RequirementExtractionTaskWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "RequirementExtractionTask";
  static readonly readSchema = RequirementExtractionTaskReadAPISchema;
  static readonly writeSchema = RequirementExtractionTaskWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    history: { target: "TaskHistory", cardinality: "one", isParent: false, relClass: "HasThread", direction: "out" },
    edits_nodes: { target: "ScopedNode", cardinality: "many", isParent: false, relClass: "EditsNode", direction: "out" },
    references_nodes: { target: "ScopedNode", cardinality: "many", isParent: false, relClass: "ReferencesNode", direction: "out" },
    analyses: { target: "ScopedNode", cardinality: "many", isParent: false, relClass: "AnalysesContent", direction: "out" },
    extracts_nodes: { target: "ScopedNode", cardinality: "many", isParent: false, relClass: "ExtractsNode", direction: "out" },
    island_pins: { target: "IslandPin", cardinality: "many", isParent: false, relClass: "IslandHoldsPin", direction: "out" },
  };

  protected get modelName(): string {
    return 'RequirementExtractionTask';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return RequirementExtractionTask.label;
  }


  constructor(data: Partial<RequirementExtractionTaskWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: RequirementExtractionTaskReadAPI, loadContext?: Record<string, any>): RequirementExtractionTask {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { security_scope, history, edits_nodes, references_nodes, analyses, extracts_nodes, island_pins, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = RequirementExtractionTaskReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, security_scope: security_scope, history: history, edits_nodes: edits_nodes, references_nodes: references_nodes, analyses: analyses, extracts_nodes: extracts_nodes, island_pins: island_pins };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return RequirementExtractionTask.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'history', 'TaskHistory', ModelRegistry);
    loadManyRelationship(processed, 'edits_nodes', 'ScopedNode', ModelRegistry);
    loadManyRelationship(processed, 'references_nodes', 'ScopedNode', ModelRegistry);
    loadManyRelationship(processed, 'analyses', 'ScopedNode', ModelRegistry);
    loadManyRelationship(processed, 'extracts_nodes', 'ScopedNode', ModelRegistry);
    loadManyRelationship(processed, 'island_pins', 'IslandPin', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new RequirementExtractionTask(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'history' - Python ORM style (cached)
  get history(): RelationshipManager<this, TaskHistory> {
    return this.getOrCreateRelationshipManager('history', () => {
      return new RelationshipManager(this, 'history', 'one', HasThread as typeof Relationship);
    });
  }

  // Relationship manager for 'edits_nodes' - Python ORM style (cached)
  get edits_nodes(): RelationshipManager<this, ScopedNode> {
    return this.getOrCreateRelationshipManager('edits_nodes', () => {
      return new RelationshipManager(this, 'edits_nodes', 'many', EditsNode as typeof Relationship);
    });
  }

  // Relationship manager for 'references_nodes' - Python ORM style (cached)
  get references_nodes(): RelationshipManager<this, ScopedNode> {
    return this.getOrCreateRelationshipManager('references_nodes', () => {
      return new RelationshipManager(this, 'references_nodes', 'many', ReferencesNode as typeof Relationship);
    });
  }

  // Relationship manager for 'analyses' - Python ORM style (cached)
  get analyses(): RelationshipManager<this, ScopedNode> {
    return this.getOrCreateRelationshipManager('analyses', () => {
      return new RelationshipManager(this, 'analyses', 'many', AnalysesContent as typeof Relationship);
    });
  }

  // Relationship manager for 'extracts_nodes' - Python ORM style (cached)
  get extracts_nodes(): RelationshipManager<this, ScopedNode> {
    return this.getOrCreateRelationshipManager('extracts_nodes', () => {
      return new RelationshipManager(this, 'extracts_nodes', 'many', ExtractsNode as typeof Relationship);
    });
  }

  // Relationship manager for 'island_pins' - Python ORM style (cached)
  get island_pins(): RelationshipManager<this, IslandPin> {
    return this.getOrCreateRelationshipManager('island_pins', () => {
      return new RelationshipManager(this, 'island_pins', 'many', IslandHoldsPin as typeof Relationship);
    });
  }

}
